/**
 * The environment a rule op runs in: tables, time, calendar and randomness. Ops never read a clock, a time zone or
 * a random source of their own - everything comes from the RuleContext, so the server (authoritative) and the tests
 * (recorded fixtures) can run the same op deterministically.
 */
import { createGameDefs, type GameDefs } from './defs';
import { NO_RANDOM, rngFromSource, type RuleRng, type UnitSource } from './random';

/**
 * Local calendar fields of a unix time, named like the AS3/JS Date getters the game uses with
 * `new Date(unix * 1000)`: date = getDate() (1..31), day = getDay() (0 = Sunday), month = getMonth() (0..11 - the
 * game compares getMonth() + 1), year = getFullYear(), hours = getHours(), minutes = getMinutes().
 */
export interface CalendarDate {
  date: number;
  day: number;
  month: number;
  year: number;
  hours: number;
  minutes: number;
  /** Local days since 1970-01-01 (consecutive local days differ by exactly 1). */
  dayIndex: number;
}

export interface RuleContext {
  /** Fresh DEF tables (createGameDefs); ops may read them, never mutate them. */
  readonly defs: GameDefs;
  /** Current server time in unix seconds (the original `user.unix_time`). */
  readonly now: number;
  /** The player's time zone as minutes east of UTC (= -new Date().getTimezoneOffset() on the player's device). */
  readonly tzOffsetMin: number;
  /** Calendar fields of `unix` in the player's time zone (replaces `new Date(unix * 1000).getX()` of the game). */
  calendar(unix: number): CalendarDate;
  /** The op's random numbers (FWP.true_random). */
  readonly rng: RuleRng;
}

/** Calendar fields of `unix` (seconds) at a fixed offset of `tzOffsetMin` minutes east of UTC. */
export function calendarAt(unix: number, tzOffsetMin: number): CalendarDate {
  const localSec = Number(unix) + tzOffsetMin * 60;
  const d = new Date(localSec * 1000);
  return {
    date: d.getUTCDate(),
    day: d.getUTCDay(),
    month: d.getUTCMonth(),
    year: d.getUTCFullYear(),
    hours: d.getUTCHours(),
    minutes: d.getUTCMinutes(),
    dayIndex: Math.floor(localSec / 86400),
  };
}

/** Largest time zone offsets in use (UTC-12:00 .. UTC+14:00). */
export const MIN_TZ_OFFSET_MIN = -12 * 60;
export const MAX_TZ_OFFSET_MIN = 14 * 60;

export interface RuleContextOptions {
  /** Server time, unix seconds. */
  now: number;
  /** Player time zone, minutes east of UTC (default 0 = UTC). */
  tzOffsetMin?: number;
  /** The rng of the op: a RuleRng (e.g. ScriptedRng) or a [0, 1) source. Default: no draws allowed. */
  rng?: RuleRng | UnitSource;
  /** Tables to use (default: fresh createGameDefs()). */
  defs?: GameDefs;
}

/** Builds a RuleContext; throws RangeError for a non-integer time or an impossible time zone offset. */
export function createRuleContext(opts: RuleContextOptions): RuleContext {
  const now = opts.now;
  if (!Number.isSafeInteger(now) || now < 0) throw new RangeError(`RuleContext: bad now ${now}`);
  const tz = opts.tzOffsetMin ?? 0;
  if (!Number.isInteger(tz) || tz < MIN_TZ_OFFSET_MIN || tz > MAX_TZ_OFFSET_MIN) throw new RangeError(`RuleContext: bad tzOffsetMin ${tz}`);
  const rng = opts.rng === undefined ? NO_RANDOM : typeof opts.rng === 'function' ? rngFromSource(opts.rng) : opts.rng;
  return {
    defs: opts.defs ?? createGameDefs(),
    now,
    tzOffsetMin: tz,
    calendar: (unix: number) => calendarAt(unix, tz),
    rng,
  };
}
